import { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { InstructionSheetConverterJobs } from './InstructionSheetConverterJobs';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  LinearProgress,
} from '@mui/material';
import { Upload, File, Info, RotateCcw } from 'lucide-react';
import { useAuthHeader } from '@/hooks/useAuth';
import { useS3Upload } from '@/hooks/use-s3-upload';
import {
  convertInstructionSheet,
  getConverterConfig,
  type InstructionSheetConverterRequest,
  type InstructionSheetConverterResponse,
  type ConverterConfigResponse,
  type ConverterConfig,
} from '@/lib/api/instruction-sheet-converter';
import type { SimpleAccount } from '@/lib/types';

interface InstructionSheetConverterProps {
  account: SimpleAccount;
}

export function InstructionSheetConverter({ account }: InstructionSheetConverterProps) {
  const { getAuthHeader } = useAuthHeader();
  const {
    uploadFiles,
    uploadProgress,
    isUploading,
    error: uploadError,
    clearError,
  } = useS3Upload();

  // ファイル状態（複数ファイル対応）
  const [instructionSheetFiles, setInstructionSheetFiles] = useState<File[]>([]);

  // UI状態
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [config, setConfig] = useState<ConverterConfigResponse | null>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);

  // 設定情報を取得
  useEffect(() => {
    const loadConfig = async () => {
      setIsLoadingConfig(true);
      try {
        const authHeaders = getAuthHeader();
        const configData = await getConverterConfig(account.id, authHeaders);
        setConfig(configData);
      } catch (err) {
        console.error('設定取得エラー:', err);
        setError('設定情報の取得に失敗しました');
      } finally {
        setIsLoadingConfig(false);
      }
    };

    loadConfig();
  }, [account.id, getAuthHeader]);

  // ファイル選択ハンドラー（複数ファイル対応）
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (fileList && fileList.length > 0) {
      const newFiles = Array.from(fileList);

      // 最大ファイル数チェック
      const maxFileCount = config?.config?.max_file_count || 1;
      const totalFiles = instructionSheetFiles.length + newFiles.length;
      if (totalFiles > maxFileCount) {
        setError(`最大${maxFileCount}ファイルまで選択可能です`);
        return;
      }

      // 各ファイルの検証
      const allowedExtensions = ['.csv', '.xlsx', '.xls', '.txt'];
      const maxSizeMB = config?.config?.max_file_size_mb || 10;

      for (const file of newFiles) {
        // ファイル形式チェック
        const fileExtension = file.name.toLowerCase();
        const isValidFile = allowedExtensions.some((ext) => fileExtension.endsWith(ext));

        if (!isValidFile) {
          setError(
            `${file.name}: CSV, Excel または テキストファイル (.csv, .xlsx, .xls, .txt) を選択してください`,
          );
          return;
        }

        // ファイルサイズチェック
        if (file.size > maxSizeMB * 1024 * 1024) {
          setError(`${file.name}: ファイルサイズは${maxSizeMB}MB以下にしてください`);
          return;
        }

        // 重複チェック
        if (instructionSheetFiles.some((existingFile) => existingFile.name === file.name)) {
          setError(`${file.name}: 同じ名前のファイルが既に選択されています`);
          return;
        }
      }

      // ファイル追加
      setInstructionSheetFiles((prev) => [...prev, ...newFiles]);
      setError(null);
    }
  };

  // 個別ファイル削除ハンドラー
  const handleRemoveFile = (index: number) => {
    setInstructionSheetFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // 全ファイルクリアハンドラー
  const handleClearFiles = () => {
    setInstructionSheetFiles([]);
    // ファイル入力をクリア
    const fileInput = document.getElementById('instruction-sheet-file') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // 変換実行ハンドラー（S3アップロード対応版）
  const handleConvert = async () => {
    if (instructionSheetFiles.length === 0) {
      setError('指示書ファイルを選択してください');
      return;
    }

    setIsConverting(true);
    setError(null);
    setSuccess(null);
    clearError(); // S3アップロードエラーをクリア

    try {
      const authHeaders = getAuthHeader();

      // S3アップロード実行
      const response = await uploadFiles(instructionSheetFiles, account.id, authHeaders);

      setSuccess(`変換処理を開始しました（ジョブID: ${response.job_id}）`);

      // ファイルをクリア（成功時）
      setInstructionSheetFiles([]);

      // ファイル入力をクリア
      const fileInput = document.getElementById('instruction-sheet-file') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (err) {
      console.error('変換エラー:', err);
      setError(err instanceof Error ? err.message : '変換処理でエラーが発生しました');
    } finally {
      setIsConverting(false);
    }
  };

  // ファイルドラッグ&ドロップ（複数ファイル対応）
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);

    if (files.length > 0) {
      // 最大ファイル数チェック
      const maxFileCount = config?.config?.max_file_count || 1;
      const totalFiles = instructionSheetFiles.length + files.length;
      if (totalFiles > maxFileCount) {
        setError(`最大${maxFileCount}ファイルまで選択可能です`);
        return;
      }

      // 各ファイルの検証
      const allowedExtensions = ['.csv', '.xlsx', '.xls', '.txt'];
      const maxSizeMB = config?.config?.max_file_size_mb || 10;

      for (const file of files) {
        // ファイル形式チェック
        const fileExtension = file.name.toLowerCase();
        const isValidFile = allowedExtensions.some((ext) => fileExtension.endsWith(ext));

        if (!isValidFile) {
          setError(
            `${file.name}: CSV, Excel または テキストファイル (.csv, .xlsx, .xls, .txt) を選択してください`,
          );
          return;
        }

        // ファイルサイズチェック
        if (file.size > maxSizeMB * 1024 * 1024) {
          setError(`${file.name}: ファイルサイズは${maxSizeMB}MB以下にしてください`);
          return;
        }

        // 重複チェック
        if (instructionSheetFiles.some((existingFile) => existingFile.name === file.name)) {
          setError(`${file.name}: 同じ名前のファイルが既に選択されています`);
          return;
        }
      }

      // ファイル追加
      setInstructionSheetFiles((prev) => [...prev, ...files]);
      setError(null);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Tabs defaultValue="convert" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="convert">変換実行</TabsTrigger>
          <TabsTrigger value="history">履歴</TabsTrigger>
        </TabsList>

        <TabsContent value="convert" className="mt-6">
          <Stack spacing={3}>
            {/* エラー・成功メッセージ */}
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {uploadError && (
              <Alert severity="error" onClose={clearError}>
                {uploadError}
              </Alert>
            )}
            {success && (
              <Alert severity="success" onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}

            {/* アップロード進捗表示 */}
            {isUploading && Object.keys(uploadProgress).length > 0 && (
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <CircularProgress size={20} />
                    アップロード進捗
                  </Typography>
                  {Object.entries(uploadProgress).map(([filename, progress]) => (
                    <Box key={filename} sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {filename}: {Math.round(progress)}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* 機能利用可否の表示 */}
            {config && !config.available && (
              <Alert severity="info">
                指示書変換機能は準備中です。機能をご利用になりたい場合は、管理者にお問い合わせください。
              </Alert>
            )}

            {/* 設定情報表示 */}
            {config && config.available && config.config && (
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Info size={20} />
                    <Typography variant="h6">設定情報</Typography>
                  </Box>
                  <Stack spacing={2}>
                    {config.config.description && (
                      <Box>
                        <Typography variant="body2">
                          <strong>説明:</strong> {config.config.description}
                        </Typography>
                      </Box>
                    )}
                    <Box>
                      <Typography variant="body2">
                        <strong>対応ファイルパターン:</strong>
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        {config.config.input_file_patterns.length > 0 ? (
                          config.config.input_file_patterns.map((pattern, index) => (
                            <Chip key={index} label={pattern} size="small" sx={{ mr: 1, mb: 1 }} />
                          ))
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            設定なし
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* ファイルアップロード */}
            <Card>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Upload size={20} />
                  指示書ファイル
                </Typography>

                <Box
                  sx={{
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: config?.available ? 'pointer' : 'not-allowed',
                    backgroundColor: instructionSheetFiles.length > 0 ? '#f5f5f5' : '#fafafa',
                    opacity: config?.available ? 1 : 0.5,
                    '&:hover': {
                      backgroundColor: config?.available ? '#f0f0f0' : '#fafafa',
                    },
                  }}
                  onDrop={config?.available ? handleDrop : undefined}
                  onDragOver={config?.available ? handleDragOver : undefined}
                  onClick={
                    config?.available
                      ? () => document.getElementById('instruction-sheet-file')?.click()
                      : undefined
                  }
                >
                  <input
                    id="instruction-sheet-file"
                    type="file"
                    accept=".csv,.xlsx,.xls,.txt"
                    multiple={
                      config?.config?.max_file_count ? config.config.max_file_count > 1 : false
                    }
                    onChange={handleFileSelect}
                    disabled={!config?.available}
                    style={{ display: 'none' }}
                  />

                  {instructionSheetFiles.length > 0 ? (
                    <Box>
                      <File size={48} color="#4caf50" />
                      <Typography variant="body1" sx={{ mt: 1, fontWeight: 'bold' }}>
                        {instructionSheetFiles.length}ファイル選択済み
                      </Typography>
                      <Stack spacing={1} sx={{ mt: 2 }}>
                        {instructionSheetFiles.map((file, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              p: 1,
                              backgroundColor: '#fff',
                              borderRadius: 1,
                              border: '1px solid #ddd',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <File size={16} />
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {file.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                ({(file.size / 1024 / 1024).toFixed(2)} MB)
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveFile(index);
                              }}
                            >
                              削除
                            </Button>
                          </Box>
                        ))}
                      </Stack>
                      {instructionSheetFiles.length > 0 && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClearFiles();
                          }}
                          sx={{ mt: 2 }}
                        >
                          全てクリア
                        </Button>
                      )}
                    </Box>
                  ) : (
                    <Box>
                      <Upload size={48} color="#ccc" />
                      <Typography variant="body1" sx={{ mt: 1 }}>
                        クリックまたはドラッグ&ドロップで指示書ファイルを選択
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        対応形式: CSV, Excel, テキスト (.csv, .xlsx, .xls, .txt)
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {config?.config ? (
                          <>
                            最大{config.config.max_file_count}ファイル、各
                            {config.config.max_file_size_mb}MB
                          </>
                        ) : (
                          '最大1ファイル、10MB'
                        )}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* 変換実行ボタン */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleConvert}
                disabled={
                  instructionSheetFiles.length === 0 ||
                  isConverting ||
                  isUploading ||
                  isLoadingConfig ||
                  !config?.available
                }
                startIcon={
                  isConverting || isUploading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <RotateCcw size={20} />
                  )
                }
                sx={{ minWidth: 200 }}
              >
                {isUploading ? 'アップロード中...' : isConverting ? '変換中...' : '変換実行'}
              </Button>
            </Box>
          </Stack>
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <InstructionSheetConverterJobs account={account} />
        </TabsContent>
      </Tabs>
    </Box>
  );
}
